import { toString, trim } from "lodash";
import { CHANNEL_EDIT_MESSAGE, CHANNEL_SEND_MESSAGE, CHAT_EDIT_MESSAGE, CHAT_SEND_MESSAGE, GROUP_EDIT_MESSAGE, GROUP_SEND_MESSAGE } from "../constants/methods";
import { ChannelSendMessage } from "../proto/ChannelSendMessage_pb";
import { ChatSendMessage } from "../proto/ChatSendMessage_pb";
import { Room, RoomMessage, RoomMessageType } from "../proto/Global_pb";
import { GroupSendMessage } from "../proto/GroupSendMessage_pb";
import store from "../store";
import { setMessengerEditId } from "../store/messenger/actions";
import { protoHandler } from "./proto";
import { resetSelectedMessages } from "./services";
import { generateRandomId, parseMessage } from "./utils";
import { clearDraft } from "../store/draft/actions";
import { FileSend } from "./fileHandler";
import { isImageType } from "./file";
import { protoRequestsObjectType } from "../constants/protoTable";
import { AdditionalType } from "../constants/globals";
import { MessageFacade } from "./message/messageFacade";
import { MessageDetailType } from "./message/messageBuilder";
import { updateLastMessage } from "../store/rooms/actions";

export type SendMessageParamsType = Omit<Partial<ChannelSendMessage.AsObject | GroupSendMessage.AsObject | ChatSendMessage.AsObject>, "replyTo" | "randomId">;

const detectMessageType = (defaultMessageType?: RoomMessage.AsObject["messageType"], message?: RoomMessage.AsObject["message"]): RoomMessage.AsObject["messageType"] => {
  const state = store.getState();
  const messengerAttachment = state.messenger.messengerAttachment;
  if (messengerAttachment === undefined) return defaultMessageType || RoomMessageType.TEXT;

  const isMultiFile = messengerAttachment?.length > 1;
  if (isMultiFile) return RoomMessageType.FILE;
  let newMessageType = messengerAttachment[0].getMessageType();
  if (newMessageType === RoomMessageType.FILE) {
    if (messengerAttachment[0].getRawFile().type.startsWith("audio")) {
      newMessageType = RoomMessageType.AUDIO;
    } else if (messengerAttachment[0].getRawFile().type.startsWith("video")) {
      newMessageType = RoomMessageType.VIDEO;
    }
  }
  if ((message?.length || 0) > 0) {
    newMessageType =
      // @ts-ignore
      {
        [RoomMessageType.IMAGE]: RoomMessageType.IMAGE_TEXT,
        [RoomMessageType.VIDEO]: RoomMessageType.VIDEO_TEXT,
        [RoomMessageType.AUDIO]: RoomMessageType.AUDIO_TEXT,
        [RoomMessageType.GIF]: RoomMessageType.GIF_TEXT,
        [RoomMessageType.FILE]: RoomMessageType.FILE_TEXT,
      }[newMessageType] || newMessageType;
  }
  return newMessageType;
};

const editMessage = (roomId: Room.AsObject["id"]) => {
  const message = document.getElementById("textarea_ref")?.innerHTML;
  if (!message) return;
  const state = store.getState();
  const messengerEditId = state.messenger.messengerEditId;
  if (!messengerEditId) return;
  const roomType = state.rooms.rooms[roomId].type;
  const roomDraft = parseMessage(message, { parseLinks: false, parseEmoji: false, isReplace: true });
  protoHandler({ [Room.Type.CHANNEL]: CHANNEL_EDIT_MESSAGE, [Room.Type.GROUP]: GROUP_EDIT_MESSAGE, [Room.Type.CHAT]: CHAT_EDIT_MESSAGE }[roomType], {
    roomId: roomId,
    messageId: messengerEditId,
    message: roomDraft,
  }).then(() => {
    store.dispatch(updateLastMessage({ roomId: roomId, lastMessage: roomDraft }));
  });
  resetSelectedMessages();
  store.dispatch(setMessengerEditId(undefined));
  store.dispatch(clearDraft(roomId));
};

export const getActionIdByType = (roomType: Room.AsObject["type"]): keyof protoRequestsObjectType => {
  return {
    [Room.Type.CHAT]: CHAT_SEND_MESSAGE,
    [Room.Type.GROUP]: GROUP_SEND_MESSAGE,
    [Room.Type.CHANNEL]: CHANNEL_SEND_MESSAGE,
  }[roomType];
};

export const sendMessage = async (params: SendMessageParamsType, roomId: Room.AsObject["id"], attachmentFile?: FileSend): Promise<void> => {
  const state = store.getState();
  const roomType = state.rooms.rooms[roomId]?.type;
  const readOnly = state.rooms.rooms[roomId]?.readOnly;
  const messengerAttachment = attachmentFile || params.attachment || "";
  const messengerReplyId = state.draft[roomId]?.replyTo;
  const messageType = detectMessageType(params.messageType, params.message);
  const message = trim(parseMessage(toString(params.message), { parseLinks: false, parseEmoji: false, isReplace: true }));
  const messageTextIsNull = Boolean(messageType === RoomMessageType.TEXT && !params.forwardFrom && !message);

  if (readOnly || messageTextIsNull) return;
  if (state.messenger.messengerAttachment && !messengerAttachment) {
    handleMultiFile(params, roomId, state.messenger.messengerAttachment);
    return;
  }
  const messageDetail: MessageDetailType = {
    message: {
      additionalData: params?.additionalData || "",
      additionalType: params?.additionalType || 0,
      attachment: messengerAttachment,
      replyTo: messengerReplyId || "",
      messageType: messageType,
      roomId,
      message,
      location: params?.location,
      contact: params?.contact,
      forwardFrom: params?.forwardFrom,
      randomId: generateRandomId(),
    },
    actionId: getActionIdByType(roomType),
    roomId,
    roomType,
  };
  await new MessageFacade(messageDetail).handle();
};

export const upsertMessage = (params: SendMessageParamsType): void => {
  const state = store.getState();
  if (params.roomId === undefined) {
    params.roomId = state.rooms.currentRoom!;
  }
  if (state.messenger.messengerEditId === undefined) {
    sendMessage(params, params.roomId).then();
  } else {
    editMessage(params.roomId);
  }
};

export const sendMultiFileMessage = (files: Array<File>): Promise<void> => {
  const roomId = store?.getState()?.rooms?.currentRoom! as string;
  const messages: Array<Promise<void>> = [];
  files.forEach(async (file: File) => {
    const attachment = new FileSend(file, isImageType(file.type) ? RoomMessageType.IMAGE : RoomMessageType.FILE) as FileSend;
    const message = {
      additionalData: "",
      additionalType: AdditionalType.NONE,
      attachment: "",
      replyTo: "",
      messageType: RoomMessageType.FILE,
      roomId,
      message: "",
    };
    messages.push(sendMessage(message, roomId, attachment));
  });
  return Promise.all(messages).then();
};

export const handleMultiFile = (params: SendMessageParamsType, roomId: Room.AsObject["id"], files: Array<FileSend>): void => {
  files.forEach(async (file) => {
    await sendMessage(params, roomId, file).then();
  });
};
